import {
  AnnotationIcon,
  ArchiveIcon,
  BookOpenIcon,
  CheckCircleIcon,
  ClipboardListIcon,
  ClockIcon,
  InformationCircleIcon,
  MailIcon,
  MicrophoneIcon,
  NewspaperIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  VideoCameraIcon,
  ViewListIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import { useState } from 'react';
import { FaCompressArrowsAlt, FaExpandArrowsAlt } from 'react-icons/fa';
import { FaLinkedinIn, FaXTwitter } from 'react-icons/fa6';
import { Audio } from 'types';

import Button from '@/components/buttons/Button';

import AIContentItem from './AIContentItem';

const AIContent = ({ audio }: { audio: Audio }) => {
  const [summary, setSummary] = useState(audio.summary);
  const [title, setTitle] = useState(audio.title);
  const [description, setDescription] = useState(audio.description);
  const [twitterThread, setTwitterThread] = useState(audio.twitterThread);
  const [linkedinPost, setLinkedinPost] = useState(audio.linkedin_post);
  const [newsletter, setNewsletter] = useState(audio.newsletter);
  const [article, setArticle] = useState(audio.article);
  const [showNotes, setShowNotes] = useState(audio.show_notes);
  const [timestampedOverview, setTimestampedOverview] = useState(
    audio.timestamped_overview
  );
  const [actionItems, setActionItems] = useState(audio.action_items);
  const [topicsAndBullets, setTopicsAndBullets] = useState(
    audio.topics_and_bullets
  );
  const [keywords, setKeywords] = useState(audio.keywords);
  const [introduction, setIntroduction] = useState(audio.introduction);
  const [reelScript, setReelScript] = useState(audio.reel_script);
  const [questions, setQuestions] = useState(audio.questions);
  const [isLoading, setIsLoading] = useState(() => {
    return !summary ? true : false;
  });
  const [openAll, setOpenAll] = useState(true);
  const [toggleCounter, setToggleCounter] = useState(0);
  const { t } = useLocale('transcription');

  return (
    <>
      <h2 className='mb-10 mt-10 flex items-center text-2xl font-bold text-primary-600'>
        <SparklesIcon
          className='mr-1 h-6 w-6 text-primary-500'
          aria-hidden='true'
        />
        AI Content
      </h2>
      <div className='flex justify-start'>
        <Button
          variant='simple'
          className='andu-4 mr-4 flex items-center text-[10px] md:text-xs '
          onClick={() => {
            setToggleCounter((prev) => prev + 1);
            setOpenAll(false);
          }}
        >
          <FaCompressArrowsAlt className='mr-1 h-3 w-3' aria-hidden='true' />
          <span className=''>{t('Collapse All')}</span>
        </Button>
        <Button
          variant='simple'
          className='andu-4 mr-4 flex items-center text-[10px] md:text-xs '
          onClick={() => {
            setToggleCounter((prev) => prev + 1);
            setOpenAll(true);
          }}
        >
          <FaExpandArrowsAlt className='mr-1 h-3 w-3' aria-hidden='true' />
          <span className=''>{t('Expand All')}</span>
        </Button>
      </div>
      <AIContentItem
        icon={
          <ArchiveIcon
            className='mr-1 h-5 w-5 text-primary-500'
            aria-hidden='true'
          />
        }
        label='Summary'
        isLoading={isLoading}
        item={summary as string}
        isAllOpened={openAll}
        toggleCounter={toggleCounter}
      />

      <AIContentItem
        icon={
          <NewspaperIcon
            className='mr-1 h-5 w-5 text-primary-500'
            aria-hidden='true'
          />
        }
        label={audio.audio_type === t('Podcast') ? 'Episode Title' : 'Title'}
        isLoading={isLoading}
        item={title as string}
        isAllOpened={openAll}
        toggleCounter={toggleCounter}
      />

      {(audio.audio_type === t('Podcast') ||
        audio.audio_type === 'Podcast') && (
        <AIContentItem
          icon={
            <InformationCircleIcon
              className='mr-1 h-5 w-5 text-primary-500'
              aria-hidden='true'
            />
          }
          label='Intoduction'
          isLoading={isLoading}
          item={introduction as string}
          isAllOpened={openAll}
          toggleCounter={toggleCounter}
        />
      )}
      {(audio.audio_type === t('Podcast') ||
        audio.audio_type === 'Podcast') && (
        <AIContentItem
          icon={
            <MicrophoneIcon
              className='mr-1 h-5 w-5 text-primary-500'
              aria-hidden='true'
            />
          }
          label='Show Notes'
          isLoading={isLoading}
          item={showNotes as string}
          isAllOpened={openAll}
          toggleCounter={toggleCounter}
        />
      )}

      <AIContentItem
        icon={
          <ClockIcon
            className='mr-1 h-5 w-5 text-primary-500'
            aria-hidden='true'
          />
        }
        label='Timestamped Overview'
        isLoading={isLoading}
        item={timestampedOverview as string}
        isAllOpened={openAll}
        toggleCounter={toggleCounter}
      />

      {(audio.audio_type === t('Meeting') ||
        audio.audio_type === 'Meeting') && (
        <AIContentItem
          icon={
            <CheckCircleIcon
              className='mr-1 h-5 w-5 text-primary-500'
              aria-hidden='true'
            />
          }
          label='Action Items'
          isLoading={isLoading}
          item={actionItems as string}
          isAllOpened={openAll}
          toggleCounter={toggleCounter}
        />
      )}

      <AIContentItem
        icon={
          <ViewListIcon
            className='mr-1 h-5 w-5 text-primary-500'
            aria-hidden='true'
          />
        }
        label='Topics and Bullets'
        isLoading={isLoading}
        item={topicsAndBullets as string}
        isAllOpened={openAll}
        toggleCounter={toggleCounter}
      />

      <AIContentItem
        icon={
          <ClipboardListIcon
            className='mr-1 h-5 w-5 text-primary-500'
            aria-hidden='true'
          />
        }
        label={
          audio.audio_type === t('Podcast')
            ? 'Episode Description'
            : 'Description'
        }
        isLoading={isLoading}
        item={description as string}
        isAllOpened={openAll}
        toggleCounter={toggleCounter}
      />

      {(audio.audio_type === t('Podcast') ||
        audio.audio_type === 'Podcast') && (
        <AIContentItem
          icon={
            <FaXTwitter
              className='mr-1 h-5 w-5 text-primary-500'
              aria-hidden='true'
            />
          }
          label='Twitter Thread'
          isLoading={isLoading}
          item={twitterThread as string}
          isAllOpened={openAll}
          toggleCounter={toggleCounter}
        />
      )}

      {(audio.audio_type === t('Podcast') ||
        audio.audio_type === 'Podcast') && (
        <AIContentItem
          icon={
            <VideoCameraIcon
              className='mr-1 h-5 w-5 text-primary-500'
              aria-hidden='true'
            />
          }
          label='Reel Script'
          isLoading={isLoading}
          item={reelScript as string}
          isAllOpened={openAll}
          toggleCounter={toggleCounter}
        />
      )}

      {(audio.audio_type === t('Podcast') ||
        audio.audio_type === 'Podcast') && (
        <AIContentItem
          icon={
            <QuestionMarkCircleIcon
              className='mr-1 h-5 w-5 text-primary-500'
              aria-hidden='true'
            />
          }
          label='Questions'
          isLoading={isLoading}
          item={questions as string}
          isAllOpened={openAll}
          toggleCounter={toggleCounter}
        />
      )}

      <AIContentItem
        icon={
          <FaLinkedinIn
            className='mr-1 h-5 w-5 text-primary-500'
            aria-hidden='true'
          />
        }
        label='Linkedin Post'
        isLoading={isLoading}
        item={linkedinPost as string}
        isAllOpened={openAll}
        toggleCounter={toggleCounter}
      />

      {(audio.audio_type === t('Podcast') ||
        audio.audio_type === 'Podcast') && (
        <AIContentItem
          icon={
            <MailIcon
              className='mr-1 h-5 w-5 text-primary-500'
              aria-hidden='true'
            />
          }
          label='Newsletter'
          isLoading={isLoading}
          item={newsletter as string}
          isAllOpened={openAll}
          toggleCounter={toggleCounter}
        />
      )}

      {(audio.audio_type === t('Podcast') ||
        audio.audio_type === 'Podcast') && (
        <AIContentItem
          icon={
            <BookOpenIcon
              className='mr-1 h-5 w-5 text-primary-500'
              aria-hidden='true'
            />
          }
          label='Article'
          isLoading={isLoading}
          item={article as string}
          isAllOpened={openAll}
          toggleCounter={toggleCounter}
        />
      )}
      <AIContentItem
        icon={
          <AnnotationIcon
            className='mr-1 h-5 w-5 text-primary-500'
            aria-hidden='true'
          />
        }
        label='Keywords'
        isLoading={isLoading}
        item={keywords as string}
        isAllOpened={openAll}
        toggleCounter={toggleCounter}
      />
    </>
  );
};

export default AIContent;
